import { useState } from 'react';
import { useIntl } from 'react-intl';
import { AnnotationPayload } from '@rossum/api-client/annotations';
import { AnnotationStatus } from '@rossum/api-client/shared';
import { IconInfoSquareRounded } from '@rossum/ui/icons/tabler';
import {
  Checkbox,
  Stack,
  SvgIcon,
  Tooltip,
  Typography,
} from '@rossum/ui/material';
import { handleTooManyImportsError } from '../../../lib/errorHandlers';
import { MoveAnnotationsMeta } from '../../../redux/modules/annotations/types';
import { QueueDialog } from '../../queues/select-queue/QueueDialog';
import { usePatchAnnotations } from './hooks/usePatchAnnotations';

export const getAnnotationToMovePayload = (
  defaultData: {
    queue: string;
    schema: string;
  },
  status: string,
  resetData: boolean
): AnnotationPayload => {
  if (resetData) {
    return {
      ...defaultData,
      rirPollId: null,
      status: 'importing',
      messages: [],
    };
  }

  return {
    ...defaultData,
    status: status === 'failed_import' ? 'failed_import' : 'to_review',
  };
};

type MoveAnnotationDialogProps = {
  selectedAnnotations: { id: number; status: AnnotationStatus }[];
  canResetData: boolean;
  onCancel: () => void;
  onSettled?: () => void;
  onSuccess?: () => void;
};

export const MoveAnnotationDialog = ({
  selectedAnnotations,
  canResetData,
  onCancel,
  onSettled,
  onSuccess,
}: MoveAnnotationDialogProps) => {
  const intl = useIntl();

  const open = selectedAnnotations.length > 0;
  const [shouldResetData, setShouldResetData] = useState(false);

  const { mutate: moveAnnotations, isLoading } = usePatchAnnotations();

  const onSubmit = ({
    queueUrl: queue,
    schemaUrl: schema,
  }: Omit<MoveAnnotationsMeta, 'resetData'>) => {
    const defaultData = { queue, schema };

    const annotationToPatch = selectedAnnotations.map(({ id, status }) => ({
      id,
      payload: getAnnotationToMovePayload(defaultData, status, shouldResetData),
    }));

    return moveAnnotations(annotationToPatch, {
      onSettled,
      onSuccess,
      onError: shouldResetData
        ? (error: unknown) => {
            handleTooManyImportsError(error, intl);
          }
        : undefined,
    });
  };

  return (
    <QueueDialog
      open={open}
      onClose={onCancel}
      isLoading={isLoading}
      title={intl.formatMessage(
        { id: 'components.modal.moveDocumentTo.title' },
        { count: selectedAnnotations.length }
      )}
      onConfirm={selectedQueue => {
        onSubmit({
          queueUrl: selectedQueue.url,
          schemaUrl: selectedQueue.schema,
        });
      }}
      tertiaryAction={
        canResetData ? (
          <Stack direction="row" mr="auto" alignItems="center">
            <Checkbox
              id="resetData"
              checked={shouldResetData}
              onChange={() => setShouldResetData(!shouldResetData)}
              size="small"
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                sx={{ color: 'text.primary', cursor: 'pointer' }}
                variant="body2"
                component="label"
                htmlFor="resetData"
              >
                {intl.formatMessage({
                  id: 'components.selectQueueModal.resetData',
                })}
              </Typography>
              <Tooltip
                title={intl.formatMessage({
                  id: 'components.selectQueueModal.tooltip',
                })}
              >
                <SvgIcon fontSize="medium" color="secondary">
                  <IconInfoSquareRounded />
                </SvgIcon>
              </Tooltip>
            </Stack>
          </Stack>
        ) : undefined
      }
    />
  );
};
